import { ReactElement, useCallback, useMemo } from 'react';

import { MedicalDocumentSimple } from '@dreryk/egabinet-document-repository-frontend-api';
import { Patient } from '@dreryk/egabinet-patient-frontend-api';

import { dateTimeFormatter } from '@dreryk/react-common';
import {
   AutoLayout,
   createDynamicColumnsWidth,
   EmptyStatePlaceholder,
   JustifyContent,
   StyledMargin,
   Table,
   TableColumnConfiguration,
   ValueRendererProps,
} from '@dreryk/react-components';

import { patientPaths } from '~libs.desktop.shared.paths';
import { PractitionerSafePresenter } from '~libs.desktop.shared.practitioner-safe-presenter';

import {
   patientMedicalDocumentRepositoryTranslationKeys,
   patientMedicalInformationTranslationKeys,
} from '../../translations';
import { MedicalDocumentShowRemovedCheckbox } from '../MedicalDocumentShowRemovedCheckbox';

import { ActionsCell } from './ActionsCell';
import { DocumentTypeCell } from './DocumentTypeCell';
import { DocumentVersionCell } from './DocumentVersionCell';
import {
   useMedicalDocumentRepositoryDataConfiguration,
   useRemovedMedicalDocumentVisibilityPreference,
} from './MedicalDocumentRepositoryTable.hooks';

type MedicalDocumentRepositoryTableProps = {
   readonly patientId: Patient['id'];
};

const actionsRenderer = (props: ValueRendererProps<MedicalDocumentSimple, string>): ReactElement | null => (
   <ActionsCell medicalDocument={props.bean} />
);

const columnsConfiguration = createDynamicColumnsWidth({
   documentType: '35%',
   creationDate: '40%',
   author: '25%',
   actions: '210px',
   version: '185px',
});

const createColumns = (): TableColumnConfiguration<MedicalDocumentSimple>[] => {
   return [
      {
         accessor: 'documentType',
         label: patientMedicalDocumentRepositoryTranslationKeys.table.documentType,
         width: columnsConfiguration.documentType,

         renderer: (props: ValueRendererProps<MedicalDocumentSimple>): JSX.Element => (
            <DocumentTypeCell name={props.bean.name} removed={props.bean.removed} />
         ),
      },
      {
         accessor: 'creationDate',
         label: patientMedicalDocumentRepositoryTranslationKeys.table.creationDate,
         width: columnsConfiguration.creationDate,
         formatter: dateTimeFormatter,
      },
      {
         accessor: (bean) => bean.createdBy,
         name: 'author',
         label: patientMedicalDocumentRepositoryTranslationKeys.table.documentAuthor,
         width: columnsConfiguration.author,

         renderer: (props: ValueRendererProps<MedicalDocumentSimple>): JSX.Element => (
            <PractitionerSafePresenter
               testId={'author'}
               practitioner={props.bean.createdBy}
               type={'standalone'}
               withIdentification
            />
         ),
      },
      {
         accessor: (bean) => bean.createdBy,
         name: 'version',
         label: patientMedicalDocumentRepositoryTranslationKeys.table.documentVersion,
         width: columnsConfiguration.version,

         renderer: (props: ValueRendererProps<MedicalDocumentSimple>): JSX.Element => (
            <DocumentVersionCell version={props.bean.version} changeReason={props.bean.changeReason} />
         ),
         sortingDisabled: true,
      },
      {
         name: 'actions',
         accessor: 'url',
         label: '',
         width: columnsConfiguration.actions,
         sortingDisabled: true,
         justifyContent: JustifyContent.END,
         renderer: actionsRenderer,
      },
   ];
};

const MedicalDocumentRepositoryTable = (props: MedicalDocumentRepositoryTableProps): JSX.Element => {
   const columns = useMemo(() => createColumns(), []);
   const { documentId } = patientPaths.useMedicalDocumentRepositorySearchParams();

   const {
      preferRemovedMedicalDocumentVisible,
      setRemovedMedicalDocumentVisibilityPreference,
   } = useRemovedMedicalDocumentVisibilityPreference();

   const handleRemovedMedicalDocumentVisibilityChange = useCallback(
      (visible: boolean) => {
         setRemovedMedicalDocumentVisibilityPreference(visible);
      },
      [setRemovedMedicalDocumentVisibilityPreference]
   );

   const dataConfiguration = useMedicalDocumentRepositoryDataConfiguration(
      props.patientId,
      preferRemovedMedicalDocumentVisible ?? false
   );

   return (
      <>
         <StyledMargin bottom={12}>
            <AutoLayout as={'span'} $direction={'horizontal'} $alignement={'rightBaseline'}>
               <MedicalDocumentShowRemovedCheckbox
                  preferRemovedMedicalDocumentVisible={Boolean(preferRemovedMedicalDocumentVisible)}
                  onRemovedMedicalDocumentVisibilityChange={handleRemovedMedicalDocumentVisibilityChange}
               />
            </AutoLayout>
         </StyledMargin>
         <Table<MedicalDocumentSimple>
            testId='medicalDocumentRepository'
            keyExtractor={'id'}
            autoScroll={'start'}
            highlightedRowKey={documentId}
            columns={columns}
            dataConfiguration={dataConfiguration}
            placeholder={
               <EmptyStatePlaceholder
                  heading={patientMedicalInformationTranslationKeys.noEntries}
                  subheading={patientMedicalInformationTranslationKeys.patientHasNoEntries}
                  testId={'noEntries'}
               />
            }
         />
      </>
   );
};

export { MedicalDocumentRepositoryTable };
